import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { MessageCircle, Check, Search, BellRing, Phone, ChevronDown } from "lucide-react";
import { toast } from "sonner";

function waLink(phone, owner, pet, lines) {
  const clean = (phone || "").replace(/\D/g, "");
  const msg = `Hi ${owner || "there"}, this is bark'ode Pet Hypermarket. Friendly reminder for ${pet}:\n${lines.map((l) => `• ${l.kind} — ${l.item} (due ${l.next_due})`).join("\n")}\nPlease book an appointment when convenient. 🐾`;
  return `https://wa.me/${clean}?text=${encodeURIComponent(msg)}`;
}

export default function Reminders() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("due");

  const load = async () => setItems((await api.get("/reminders")).data);
  useEffect(() => { load(); }, []);

  const filtered = items.filter((r) => {
    if (tab === "overdue" && !r.overdue) return false;
    if (tab === "due" && (r.sent_at || r.overdue)) return false;
    if (tab === "sent" && !r.sent_at) return false;
    if (q) {
      const s = q.toLowerCase();
      if (!`${r.pet_name} ${r.owner_name} ${r.phone} ${r.item}`.toLowerCase().includes(s)) return false;
    }
    return true;
  });

  // Group by patient_id
  const grouped = useMemo(() => {
    const map = new Map();
    filtered.forEach((r) => {
      const key = r.patient_id;
      if (!map.has(key)) map.set(key, { patient_id: r.patient_id, pet_name: r.pet_name, owner_name: r.owner_name, phone: r.phone, case_number: r.case_number, reminders: [] });
      map.get(key).reminders.push(r);
    });
    const arr = Array.from(map.values());
    arr.forEach((g) => g.reminders.sort((a, b) => (a.next_due || "").localeCompare(b.next_due || "")));
    arr.sort((a, b) => (a.reminders[0]?.next_due || "").localeCompare(b.reminders[0]?.next_due || ""));
    return arr;
  }, [filtered]);

  const markSent = async (r) => {
    try { await api.post("/reminders/mark-sent", { record_id: r.record_id, channel: "whatsapp" }); toast.success("Marked as sent"); load(); }
    catch { toast.error("Failed"); }
  };

  return (
    <div className="p-4 md:p-8 max-w-[1300px] mx-auto animate-fade-in">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h1 className="font-primary text-3xl md:text-4xl font-bold tracking-tight">Reminders</h1>
          <p className="text-sm text-muted-foreground mt-1">Grouped by patient. Send a single message covering all due reminders.</p>
        </div>
      </div>

      <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 px-4 py-3 mb-5 text-sm flex items-start gap-3">
        <BellRing className="h-4 w-4 mt-0.5 text-primary" />
        <div>WhatsApp auto-send is queued. Until messaging credentials are configured, tap <span className="font-medium">Send via WhatsApp</span> to open the chat with a prefilled message on your device.</div>
      </div>

      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {[["due","Upcoming"],["overdue","Overdue"],["sent","Sent"],["all","All"]].map(([k,l]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`reminders-tab-${k}`}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${tab === k ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}>
            {l}
          </button>
        ))}
        <div className="relative flex-1 min-w-[200px] max-w-md ml-auto">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input className="pl-9" placeholder="Filter by pet, owner, phone…" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
      </div>

      <div className="space-y-3">
        {grouped.length === 0 && (
          <Card className="p-12 text-center text-muted-foreground border-border/60"><BellRing className="h-10 w-10 mx-auto opacity-40 mb-2" />No reminders here.</Card>
        )}
        {grouped.map((g) => (
          <Card key={g.patient_id} className="p-5 border-border/60">
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
              <div>
                <Link to={`/patients/${g.patient_id}`} className="font-primary font-semibold text-lg hover:underline">{g.pet_name}</Link>
                <div className="text-xs text-muted-foreground">{g.owner_name} · <Phone className="inline h-3 w-3" /> {g.phone} · {g.case_number}</div>
              </div>
              {g.phone && (
                <a href={waLink(g.phone, g.owner_name, g.pet_name, g.reminders.filter((r) => !r.sent_at))} target="_blank" rel="noreferrer">
                  <Button size="sm" data-testid={`wa-group-${g.patient_id}`}><MessageCircle className="h-4 w-4 mr-1" />Send all via WhatsApp</Button>
                </a>
              )}
            </div>
            <div className="divide-y divide-border/70 border-t border-border/70">
              {g.reminders.map((r) => (
                <div key={r.id} className="flex items-center gap-3 py-2.5">
                  <div className="w-16 text-center">
                    <div className="text-[10px] text-muted-foreground">{r.next_due && new Date(r.next_due).toLocaleDateString(undefined, { month: "short" }).toUpperCase()}</div>
                    <div className="font-primary text-lg font-bold leading-none">{r.next_due ? new Date(r.next_due).getDate() : "—"}</div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm">{r.item}</span>
                      <Badge variant="outline" className="text-xs">{r.kind}</Badge>
                      {r.overdue && !r.sent_at && <Badge className="bg-destructive text-destructive-foreground text-xs">Overdue</Badge>}
                      {r.sent_at && <Badge className="bg-emerald-600 text-white text-xs">Sent</Badge>}
                    </div>
                  </div>
                  {!r.sent_at && (
                    <Button size="sm" variant="ghost" onClick={() => markSent(r)} data-testid={`mark-sent-${r.record_id}`}><Check className="h-4 w-4 mr-1" />Mark sent</Button>
                  )}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
